import React, { useRef, useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Form, Select, Upload, Row, Col, Card, notification, Breadcrumb } from "antd";
import { InboxOutlined, ArrowLeftOutlined, HomeOutlined, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import FileListDrawer from "./DownloadTemplate"; // Import the drawer component
import { decodeToken } from "react-jwt";
import documentManagementAPI from "../../../../restAPI/DocumentManagement";
import { useNavigate } from 'react-router-dom';


const Context = React.createContext({
  name: "Default",
});
const { Dragger } = Upload;

const UploadReceivable = () => {
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [showUploadList, setShowUploadList] = useState(true);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachment] = useState([]);
  const [transactionType, setTransactionType] = useState("Accounts receivable");
  const [api, contextHolder] = notification.useNotification();
  const [isbtnActive, setIsbtnActive] = useState(false);
  const [loading, setLoading] = useState(false); // for loading effect
  const formRef = useRef(null);


  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement,
    });
  };

  useEffect(() => {
    //decode token
    if (token != null) {
      const decodedTokenValue = decodeToken(token);
    }
  }, [token]);

  const validateFile = (file, currentAttachments) => {
    const excelTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];
    const pdfType = "application/pdf";

    const isExcel = excelTypes.includes(file.type);
    const isPDF = file.type === pdfType;
    const isFileSizeValid = file.size / 1024 / 1024 < 15;

    if (!isExcel && !isPDF) {
      setErrors({
        attachments: `${file.name} is not a valid file type. Only PDF and Excel files are allowed.`,
      });
      return false;
    }

    if (!isFileSizeValid) {
      setErrors({
        attachments: `${file.name} is larger than 5MB. Please upload a smaller file.`,
      });
      return false;
    }

    const hasExcel = currentAttachments.some(f => excelTypes.includes(f.type));
    const hasPDF = currentAttachments.some(f => f.type === pdfType);
    const pdfCount = currentAttachments.filter(f => f.type === pdfType).length;

    if ((isExcel && hasPDF) || (isPDF && hasExcel)) {
      setErrors({
        attachments: "You cannot upload both PDF and Excel files together. Upload only one type at a time.",
      });
      return false;
    }

    if (isExcel && hasExcel) {
      setErrors({
        attachments: "Only one Excel file is allowed.",
      });
      return false;
    }

    if (isPDF && pdfCount >= 10) {
      setErrors({
        attachments: "Only 10 PDF files are allowed.",
      });
      return false;
    }

    setErrors({
      attachments: ""
    });
    return true;
  };


  const props = {
    name: "file",
    multiple: true,
    showUploadList: { showUploadList },
    accept: ".pdf,.xls,.xlsx",
    onRemove: (file) => {
      const updatedAttachments = attachments.filter((item) => item.uid !== file.uid);
      setAttachment(updatedAttachments);
      setIsbtnActive(updatedAttachments.length > 0);
    },
    beforeUpload: (file, fileList) => {
      let newAttachments = [...attachments];

      for (let i = 0; i < fileList.length; i++) {
        const currentFile = fileList[i];
        if (!validateFile(currentFile, newAttachments)) {
          return Upload.LIST_IGNORE;
        }
        newAttachments.push(currentFile);
      }
      setAttachment(newAttachments);
      setIsbtnActive(newAttachments.length > 0);
      return false;
    },
    attachments,
  };

  const handleClearFields = () => {
    setAttachment([]);
    setIsbtnActive(false);
    formRef.current.resetFields(["attachments"]);
    setErrors({
      attachments: ""
    });
  };

  const handleBack = () => {
    navigate('/documentupload/AccountReceivable');
  }

  const handleUpload = async () => {

    if (localStorage.getItem("groupId") != null || localStorage.getItem("groupId") != undefined) {
      if (localStorage.getItem("companyId") != null || localStorage.getItem("companyId") != undefined) {
        const formData = new FormData();

        formData.append("groupId", localStorage.getItem("groupId"));
        formData.append("companyId", localStorage.getItem("companyId"));
        formData.append("transactionType", transactionType);

        attachments.forEach((file) => {
          if (
            file.type === "application/vnd.ms-excel" ||
            file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          ) {
            formData.append("excel", file);
          } else if (file.type === "application/pdf") {
            formData.append("pdfs", file);
          }
        });
        if (attachments.length > 0) {
          setLoading(true); // start loading
          await documentManagementAPI
            .uploadDocument(
              token,
              formData
            )
            .then((response) => {
              handleClearFields();
              openNotificationWithIcon(
                "success",
                "Successful",
                `${response.message}`,
                "bottomLeft"
              );
            })
            .catch((err) => {
              if (err.response) {
                openNotificationWithIcon(
                  "error",
                  "Extracting records failed",
                  `${err.response.data.message}`,
                  "bottomLeft"
                );
              } else {
                openNotificationWithIcon(
                  "error",
                  "Extracting records failed",
                  `${err.message}`,
                  "bottomLeft"
                );
              }
            }).finally(() => {
              setLoading(false); // stop loading
            });
        }
      }
      else {
        openNotificationWithIcon(
          "error",
          "Extracting records failed",
          "Please select a Company.",
          "bottomLeft"
        );
      }

    }
    else {
      openNotificationWithIcon(
        "error",
        "Extracting records failed",
        "Please select a Group.",
        "bottomLeft"
      );
    }

  }

  const contextValue = useMemo(() => ({ name: "Ant Design" }), []);

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Row >
        <Col xs={24} sm={18} md={24} lg={24}>
          <Breadcrumb style={{ marginBottom: "16px" }}>
            <Breadcrumb.Item href="/">
              <HomeOutlined />
            </Breadcrumb.Item>
            <Breadcrumb.Item>Document Management</Breadcrumb.Item>
            <Breadcrumb.Item>Accounts Receivable</Breadcrumb.Item>
            <Breadcrumb.Item>Document Upload</Breadcrumb.Item>
          </Breadcrumb>
          <div className="border-container">
            <Button
              type="primary"
              variant="dashed"
              className="card-button"
              size="large"
              icon={<ArrowLeftOutlined />}
              onClick={() => handleBack()}
              shape="circle"
            />
            <Card bordered title="Document Upload"
              extra={
                <Button
                  type="primary"
                  style={{ position: "relative", top: "-10px" }}
                  onClick={() => setDrawerVisible(true)}
                  icon={<DownloadOutlined />}

                >
                  Template
                </Button>
              }
            >
              {/* Section 1 */}
              <Row>
                <Col xl={6}>
                </Col>
                <Col xl={12}>

                  <Form layout="vertical"
                    ref={formRef}
                  >
                    <Form.Item
                      label="Transaction Type"
                      name="transactionType"
                      rules={[{ required: true, message: "Transaction Type is required" }]}
                    >
                      <Select
                        placeholder="Select Template Type"
                        size="large"
                        disabled={true}
                        defaultValue={transactionType}
                        options={[
                          { value: "Accounts receivable", label: "Accounts Receivable" },
                          { value: "Accounts payable", label: "Accounts Payable" },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Upload File(s)"
                      name="attachments"
                      rules={[{ required: true, message: "File is required" }]}
                      validateStatus={errors.attachments ? "error" : ""}
                      help={errors.attachments || ""}
                    >
                      <Dragger
                        name="files"
                        {...props}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file(s) to this area to upload.</p>
                        <p className="ant-upload-hint">Supported formats: Single Excel file (.xlsx) or multiple PDF files (.pdf).</p>
                      </Dragger>
                    </Form.Item>
                  </Form>
                </Col>
              </Row>
              <Row justify="center" gutter={16}>
                <Col>
                  <Button
                    size="large"
                    onClick={handleClearFields}
                    style={{ marginTop: "15px" }}
                  >
                    Clear
                  </Button>
                </Col>
                <Col>
                  <Button
                    disabled={!isbtnActive}
                    onClick={!loading ? handleUpload : null}
                    type="primary"
                    block
                    htmlType="submit"
                    style={{ marginTop: "15px", cursor: loading ? 'not-allowed' : 'pointer' }}
                    size="large"
                    icon={loading ? <LoadingOutlined /> : null}>
                    {loading ? "Uploading..." : "Extract"}
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Drawer Component */}
      <FileListDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      />
    </Context.Provider>
  );
};

export default UploadReceivable;
